import { useEffect, useState } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { safeFormatDate, safeDistanceToNow } from "@/lib/dates";

const ENTITY_TYPES = ["all", "horse", "staff", "feed_log", "invoice", "tack", "vehicle", "maintenance", "executive", "whatsapp", "telegram", "settings", "chukka_rating", "health_log", "user"];

export default function AuditPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("all");
  const [actor, setActor] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (entity !== "all") params.entity = entity;
      if (actor) params.actor = actor;
      const r = await api.get("/audit/", { params });
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity]);
  useQuickEditRefresh(load, [entity]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit trail"
        subtitle="Every create, update, delete, login, ingest. fully searchable"
      />
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger className="w-[200px]" data-testid="audit-filter-entity"><SelectValue /></SelectTrigger>
          <SelectContent>{ENTITY_TYPES.map((e) => <SelectItem key={e} value={e}>{e === "all" ? "All entities" : e}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Filter by actor (e.g. whatsapp:Mosa or username)" value={actor} onChange={(e) => setActor(e.target.value)} className="max-w-[300px]" data-testid="audit-filter-actor" />
        <Button variant="secondary" onClick={load} data-testid="audit-apply-button">Apply</Button>
        <Button variant="ghost" onClick={() => { setEntity("all"); setActor(""); }}>Reset</Button>
      </div>
      <Card className="rounded-xl">
        <div className="p-4" data-testid="audit-timeline">
          {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            : items.length === 0 ? <p className="text-sm text-muted-foreground py-12 text-center">No audit entries match.</p>
            : <div className="relative">
              <div className="absolute left-[7px] top-3 bottom-3 w-px bg-border" />
              <div className="space-y-1">
                {items.map((a) => (
                  <div key={a.id} className="relative flex gap-3 px-2 py-2 hover:bg-accent/40 rounded-md" data-testid="audit-item">
                    <div className="mt-1.5 h-3.5 w-3.5 rounded-full bg-[hsl(var(--ap-brass))] border-2 border-background shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between flex-wrap gap-x-3">
                        <span className="text-sm">
                          <span className="font-medium">{a.actor}</span>{" "}
                          <span className="text-muted-foreground">{a.action}</span>{" "}
                          <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{a.entity}</span>
                          {a.entity_id && <span className="font-mono text-[10px] text-muted-foreground ml-1">{a.entity_id.slice(0, 8)}</span>}
                        </span>
                        <span className="text-xs text-muted-foreground">{safeFormatDate(a.created_at, "MMM d, HH:mm:ss")} • {safeDistanceToNow(a.created_at)} ago</span>
                      </div>
                      {(a.after?.name || a.after?.supplier || a.after?.horse || a.after?.type) && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">{a.after?.type ? `${a.after.type}: ` : ""}{a.after?.name || a.after?.supplier || a.after?.horse || ""}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          }
        </div>
      </Card>
    </div>
  );
}
